import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Switch,
  Autocomplete,
  Chip,
  FormControlLabel,
} from '@mui/material';
import type { AdminUser, Role } from 'types';
import { createUser, updateUser } from 'services/usersApi';
import { globalSnackbar } from 'components/GlobalSnackbar';
import DrawerShell, { DrawerError } from '../DrawerShell';
import { fieldSx, validateEmail } from '../shared';

interface UserDrawerProps {
  /** Редактируемый пользователь; `null` — режим создания. */
  user: AdminUser | null;
  availableRoles: Role[];
  onClose: () => void;
  onSaved: () => void;
}

interface FormErrors {
  name?: string;
  email?: string;
  roles?: string;
}

export default function UserDrawer({ user, availableRoles, onClose, onSaved }: UserDrawerProps) {
  const isEdit = user !== null;

  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [isActive, setIsActive] = useState(user?.isActive ?? true);
  const [isAdmin, setIsAdmin] = useState(user?.isAdmin ?? false);
  const [selectedRoles, setSelectedRoles] = useState<Role[]>(() =>
    user ? availableRoles.filter((r) => user.roles.some((ur) => ur.id === r.id)) : []
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!name.trim()) next.name = 'Введите имя';
    if (!email.trim()) next.email = 'Введите email';
    else if (!validateEmail(email)) next.email = 'Некорректный email';
    if (selectedRoles.length === 0) next.roles = 'Выберите хотя бы одну роль';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setApiError('');
    const payload = {
      name: name.trim(),
      email: email.trim(),
      is_active: isActive,
      is_admin: isAdmin,
      roles: selectedRoles.map((r) => r.id),
      yandex_tracker_queues: [],
    };
    try {
      if (isEdit && user) {
        await updateUser(user.id, payload);
        globalSnackbar({ message: 'Пользователь обновлён', severity: 'success' });
      } else {
        await createUser(payload);
        globalSnackbar({ message: 'Пользователь создан', severity: 'success' });
      }
      onSaved();
      onClose();
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Ошибка сохранения пользователя');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DrawerShell
      title={isEdit ? 'Редактирование пользователя' : 'Новый пользователь'}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel={isEdit ? 'Сохранить' : 'Создать'}
      submittingLabel={isEdit ? 'Сохранение...' : 'Создание...'}
    >
      <DrawerError message={apiError} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Имя"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (errors.name) setErrors((v) => ({ ...v, name: undefined }));
          }}
          size="small"
          fullWidth
          error={Boolean(errors.name)}
          helperText={errors.name}
          sx={fieldSx}
        />
        <TextField
          label="Email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (errors.email) setErrors((v) => ({ ...v, email: undefined }));
          }}
          size="small"
          fullWidth
          error={Boolean(errors.email)}
          helperText={errors.email}
          sx={fieldSx}
        />

        <Autocomplete
          multiple
          options={availableRoles}
          getOptionLabel={(option) => option.title || option.name}
          value={selectedRoles}
          onChange={(_, newValue) => {
            setSelectedRoles(newValue);
            if (errors.roles) setErrors((v) => ({ ...v, roles: undefined }));
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              // eslint-disable-next-line react/jsx-key
              <Chip size="small" label={option.title || option.name} {...getTagProps({ index })} />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Роли"
              size="small"
              error={Boolean(errors.roles)}
              helperText={errors.roles}
              sx={fieldSx}
            />
          )}
        />

        {/* is_admin */}
        <ToggleRow
          title="Администратор"
          description={isAdmin ? 'Полный доступ к админ-разделу' : 'Обычный пользователь'}
          checked={isAdmin}
          onChange={setIsAdmin}
        />

        {/* is_active */}
        <ToggleRow
          title="Статус активности"
          description={isActive ? 'Пользователь активен' : 'Пользователь отключён'}
          checked={isActive}
          onChange={setIsActive}
        />
      </Box>
    </DrawerShell>
  );
}

function ToggleRow({
  title,
  description,
  checked,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <FormControlLabel
      sx={{
        m: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1.5,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
        bgcolor: 'var(--color-bg)',
        '& .MuiFormControlLabel-label': { width: '100%' },
      }}
      labelPlacement="start"
      control={
        <Switch
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--color-primary)' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              bgcolor: 'var(--color-primary)',
            },
          }}
        />
      }
      label={
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)', mt: 0.25 }}>
            {description}
          </Typography>
        </Box>
      }
    />
  );
}
